import React from 'react';
import { Link } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { ElementType } from 'react';

export interface SubNavItem {
  id: string;
  label: string;
  icon: ElementType;
  /** Optional count badge (right-aligned). Omit for items without a count (e.g. Company). */
  count?: number;
  /** Optional alert dot (e.g. the Admin Assessments outstanding-items indicator). */
  alert?: boolean;
  /** react-router target — present when the column drives navigation via the URL. */
  to?: string;
}

interface SubNavColumnProps {
  /** Optional identity block rendered above the item list (company/admin header). */
  header?: React.ReactNode;
  items: SubNavItem[];
  activeId: string;
  /** Called when an item without a `to` is selected (non-routed / modal usage). */
  onSelect?: (id: string) => void;
  ariaLabel?: string;
  /** Column width in px; the company profile uses a slimmer 220. */
  width?: number;
}

const Column = styled('aside', { shouldForwardProp: (prop) => prop !== 'width' })<{ width?: number }>(({ width = 250 }) => ({
  width,
  flexShrink: 0,
  background: '#F5F7FA',
  borderRight: '1px solid rgba(0, 0, 0, 0.08)',
  display: 'flex',
  flexDirection: 'column',
  // Full-height non-scrolling sibling; only the content area scrolls (its own list scrolls if long).
  minHeight: 0,
  overflowY: 'auto',
}));

const HeaderSlot = styled('div')({
  borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
});

const ItemList = styled('div')({ padding: '8px 0' });

// Text colour re-asserted across every link state to out-specify styleOverrides.css anchor rules.
const itemStyles = (active?: boolean) => ({
  position: 'relative' as const,
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 20px',
  fontSize: '14px',
  fontWeight: active ? 600 : 400,
  '& svg': { fontSize: '20px', color: active ? '#37424f' : '#7a8494' },
  textDecoration: 'none',
  cursor: 'pointer',
  border: 'none',
  width: '100%',
  textAlign: 'left' as const,
  fontFamily: 'inherit',
  background: active ? '#fff' : 'transparent',
  transition: 'background 0.15s',
  '&, &:link, &:visited, &:hover, &:focus, &:active': { color: active ? '#37424f' : '#5d6675' },
  '&:hover': { background: active ? '#fff' : '#ECF0F6' },
});

const ItemLink = styled(Link, { shouldForwardProp: (prop) => prop !== 'active' })<{ active?: boolean }>(
  ({ active }) => itemStyles(active),
);

const ItemButton = styled('button', { shouldForwardProp: (prop) => prop !== 'active' })<{ active?: boolean }>(
  ({ active }) => itemStyles(active),
);

const Label = styled('span')({ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' });

const Count = styled('span', { shouldForwardProp: (prop) => prop !== 'active' })<{ active?: boolean }>(
  ({ active }) => ({ fontSize: '13px', color: active ? '#1976d2' : '#8a94a3' }),
);

const AlertDot = styled('span')({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  background: '#e8891c',
  flexShrink: 0,
});

/**
 * Persistent 250px second-level navigation column shared by the company profile
 * and the Admin area. Renders each item as a react-router link when it has a
 * `to`, or as a button calling `onSelect` otherwise (e.g. the registry modal
 * preview where deep-linking is not wanted).
 */
export default function SubNavColumn({ header, items, activeId, onSelect, ariaLabel = 'Sections', width }: SubNavColumnProps) {
  return (
    <Column aria-label={ariaLabel} width={width}>
      {header && <HeaderSlot>{header}</HeaderSlot>}
      <ItemList>
        {items.map((item) => {
          const Icon = item.icon;
          const active = item.id === activeId;
          const contents = (
            <>
              <Icon aria-hidden="true" />
              <Label>{item.label}</Label>
              {item.alert && <AlertDot aria-hidden="true" />}
              {item.count != null && <Count active={active}>{item.count}</Count>}
            </>
          );

          if (item.to) {
            return (
              <ItemLink key={item.id} to={item.to} active={active} aria-current={active ? 'page' : undefined}>
                {contents}
              </ItemLink>
            );
          }

          return (
            <ItemButton
              key={item.id}
              type="button"
              active={active}
              aria-current={active ? 'page' : undefined}
              onClick={() => onSelect?.(item.id)}
            >
              {contents}
            </ItemButton>
          );
        })}
      </ItemList>
    </Column>
  );
}
